import { ServiceDataModel, YesNoMasterListModel, MasterDataModel } from "../../services/models/Models";
import { labels, currentResource } from "../../util/utils";
import { ErrorCodes, UploadStatus, Constants, SuccessStatusCode } from "../../util/UIConstants";
import _ = require("lodash");
import { DataAddInStateManager } from "./DataAddInStateManager";
import { ServerSyncInfoSettings } from "./ServerSyncInfoSettings";
import Utility from "../../util/utilityFunctions";
import { EntityConfiguration } from "../../configurations/configuration";

export const checkExcelError = (cellValue: string) => {
  let excelErrors = ["#NAME?", "#REF!", "#VALUE!", "#DIV/0!", "#N/A!", "#NUM!", "#NULL!", "#ERROR!"];
  return excelErrors.includes(cellValue);
};
//Creating Data model to render it in excel table format.
export const translateToServiceDataModel = (
  data: any,
  engagementId: string,
  otherSourceData: any = null
): ServiceDataModel => {
  if (data) {
    let records: ServiceDataModel = new ServiceDataModel();
    records.masterData = new MasterDataModel();
    records.masterData.yesNoList = new YesNoMasterListModel();
    records.engagementId = engagementId;
    records.fetchedTimeStamp = new Date();
    records.done = true;
    records.response = data;
    if (otherSourceData) {
      records.referenceData = otherSourceData;
    }
    return records;
  } else {
    return {
      masterData: new MasterDataModel(),
      engagementId: engagementId,
      fetchedTimeStamp: new Date(),
      done: false,
      response: null,
      referenceData: null,
      apiResponseReturned: false,
    };
  }
};

//Mapping error messages on the basis of error code.
export const getErrorMessage = (errorMessage: string, entityType: number, errorCode: number = null) => {
  if (!_.isNull(errorCode)) {
    const localMessage = currentResource.errors[errorCode];
    if (localMessage) return localMessage;
  }
  const position = errorMessage.indexOf("-");
  if (position !== -1) {
    const [code, message] = errorMessage.split("-");
    const localMessage = currentResource.errors[parseInt(code)];
    if (localMessage) return localMessage;
    if (message) return message?.trim();
    return currentResource.errors[labels.unexpectedApiFailure];
  } else {
    if (entityType == 2) return labels.taskTitleRequiredError;
  }
};

export const extractErrorDetails = (errors: any, modifedRequests = []) => {
  const errorDetails = [];
  if (errors) {
    for (const key in errors) {
      let errorMessageForUser = "";
      if (errors.hasOwnProperty(key)) {
        const indexMatch = key.match(/\[(\d+)\]/);
        const index = indexMatch ? parseInt(indexMatch[1], 10) : null;

        // Skip processing if errorDetails already contains this index
        if (errorDetails.some((error) => error.index === index)) {
          continue; // Skip to the next iteration
        }

        const error = errors[key];
        if (error.errorMessage) {
          const spilitText = error?.errorMessage.split(" - ");
          if (spilitText.length > 1) {
            errorMessageForUser = currentResource.errors?.[parseInt(spilitText[0].trim())];
            if (Utility.isNullOrUndefined(errorMessageForUser)) {
              errorMessageForUser = error.errorMessage.trim();
            }
          }
        } else {
          for (const errorMessage of errors[key]) {
            const spilitText = errorMessage.split(" - ");
            if (spilitText.length > 1) {
              errorMessageForUser = currentResource.errors?.[parseInt(spilitText[0].trim())];
              if (Utility.isNullOrUndefined(errorMessageForUser)) {
                errorMessageForUser = errorMessage.trim();
              }
              break; // Exit the loop
            }
            errorMessageForUser = errorMessage.trim();
          }
        }

        errorDetails.push({
          index: index,
          errorMessage: errorMessageForUser,
        });
      }
    }
  }
  if (modifedRequests.length > 0) {
    errorDetails.forEach((item) => {
      modifedRequests[item.index].uploadStatus = UploadStatus.InvalidInput;
      modifedRequests[item.index].markForUpdate = Constants.MarkForUpdateNo;
      modifedRequests[item.index].uploadError = item.errorMessage;
    });
  }
  return errorDetails;
};

export const ExtractErros = (data: any): Array<string> => {
  let errors = new Array<string>();
  if (!_.isEmpty(data?.errors?.Error)) {
    errors = data.errors.Error;
  } else if (!_.isEmpty(data?.errors)) {
    for (let i in data.errors) {
      errors.push(data.errors[i]);
    }
  } else if (!_.isEmpty(data)) {
    errors.push(JSON.stringify(data));
  } else {
    // no errors
  }
  return errors;
};

export const getBatchSize = (configuration): number => {
  if (configuration?.entityConfiguration?.apiTemplate?.Batchsize) {
    return configuration.entityConfiguration.apiTemplate?.Batchsize;
  }
  return 100;
};

export const parseErrorData = (data: any, errorMapper?: Function) => {
  let errorMessage = "";
  let errorCode = data?.ErrorCode;
  switch (errorCode) {
    case ErrorCodes.AccessTokenGetError:
    case ErrorCodes.UniversalApiNoResponseError:
    case ErrorCodes.ApiResourceNotFound:
    case ErrorCodes.ApiValidationErrorBadRequest:
    case ErrorCodes.ApiUnhandledException:
    case ErrorCodes.ApiValidationErrorPreConditionFailed:
    case ErrorCodes.ApiFailedWithNoSuccess:
      return labels.apiServerError;
    case ErrorCodes.HerculesApiNoResponseError:
      return labels.apiServerConnectError;
    case ErrorCodes.UniversalApiNoValidDataError:
    case ErrorCodes.ApiValidationFailedUnauthorize:
      return labels.insufficientPermissionsError;
    case ErrorCodes.ApiValidationError:
      return labels.errorMessageOnExcelUpdate;
  }

  // To Do Need to refactor below logic
  if (data?.response?.data?.errors) {
    let errors = data?.response?.data?.errors;

    if (errorMapper) {
      errorMessage = errorMapper(data.response);
    } else {
      errorMessage = labels.apiServerExceptionError;
    }
  } else if (data?.response?.errors) {
    let errors = data?.response?.errors;

    if (errorMapper) {
      errorMessage = errorMapper(data.response);
    } else {
      errorMessage = labels.apiServerExceptionError;
    }
  } else if (data?.response?.data?.ErrorCode) {
    if (errorMapper) {
      errorMessage = errorMapper(data.response);
    } else {
      errorMessage = labels.apiServerExceptionError;
    }
  } else if (data?.response?.status && !SuccessStatusCode.includes(data?.response?.status)) {
    errorMessage = labels.apiServerExceptionError;
  }

  return errorMessage;
};

export const referenceSheetExists = async (sheetId: string) => {
  let referenceSheetExists = true;
  await Excel.run(async (context) => {
    const masterSheetName = `${Constants.MasterWorkSheetPrefix}`;
    let dataSheet = context.workbook.worksheets.getItemOrNullObject(masterSheetName);
    await context.sync();
    // if ref sheet not present flag refrenceSheetMissing =true
    if (dataSheet.isNullObject) {
      referenceSheetExists = false;
    }
  });
  return referenceSheetExists;
};

export const canShowLoadInfoFromSettings = async (activeSheetId, activeSheetName): Promise<ServerSyncInfoSettings> => {
  let serverSyncInfo = null;
  serverSyncInfo = await DataAddInStateManager.getPersistedServerSyncInfo(activeSheetId, activeSheetName);

  return serverSyncInfo;
};

export const validationForExistingInput = async (setLdcVersioning = null) => {
  let getSettings = false;
  let boundsheet = null;
  let engagementId = null;
  let selectedEntityType = null;
  let activeSheetsId = null;
  let error = null;

  try {
    await Excel.run(async (context) => {
      const worksheets = context.workbook.worksheets;
      worksheets.load("items");
      await context.sync();
      let settings: ServerSyncInfoSettings = null;
      for (let i = 0; i < worksheets.items.length; i++) {
        let sheetname = worksheets.items[i].name;
        activeSheetsId = worksheets.items[i].id;
        try {
          settings = await canShowLoadInfoFromSettings(activeSheetsId, sheetname);
        } catch {
          console.warn("Error loading existing settings, so skipped loading.");
        }
        if (settings != null) {
          getSettings = true;
          engagementId = settings.engagementId;
          boundsheet = worksheets.items[i].name;
          selectedEntityType = settings.selectedEntityType;
          if (setLdcVersioning) {
            setLdcVersioning({ datacenterversion: settings.engagementLDCVersion });
          }
          break;
        }
      }
      if (!getSettings) {
        activeSheetsId = null;
        boundsheet = null;
      }
    });
  } catch (e) {
    error = labels.offlineModeUserClickError;
    console.warn("Error in validationForExistingInput", e);
  } finally {
    return {
      boundsheet,
      engagementId,
      selectedEntityType,
      activeSheetsId,
      error,
    };
  }
};
